import type { CSSProperties } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import type { CampaignDto } from '@/generated/korza-api-client/types.gen';
import type { ChartGranularity } from '@/lib/chart-axis';
import { markerTime } from '@/lib/campaign-markers';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/components/metrics/formatters';

export interface HomeCampaignPopoverProps {
  /** Label of the bucket (X axis). */
  title: string;
  campaigns: CampaignDto[];
  granularity: ChartGranularity;
  currency: string;
  className?: string;
  style?: CSSProperties;
}

/**
 * Card of the campaigns of one bucket of the Início chart (HOME-04): each
 * campaign's full name, send time and "{receita} · {N} pedidos atribuídos"
 * ("—" when the campaign hides its data).
 */
export function HomeCampaignPopover({
  title,
  campaigns,
  granularity,
  currency,
  className,
  style,
}: HomeCampaignPopoverProps) {
  const { LL, locale } = useI18nContext();
  return (
    <div
      role="status"
      data-home-campaign-popover
      className={cn(
        'pointer-events-none w-64 max-w-full rounded-md border bg-popover p-3 text-popover-foreground shadow-md',
        className
      )}
      style={style}
    >
      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {title}
      </p>
      <ul className="mt-1 flex flex-col">
        {campaigns.map((campaign) => (
          <li key={campaign.id} className="border-t pt-2 mt-2 first:mt-1">
            <span className="flex items-baseline justify-between gap-3 text-sm font-semibold">
              <span className="min-w-0 break-words">{campaign.name}</span>
              <span className="shrink-0 font-medium text-muted-foreground tabular-nums">
                {markerTime(campaign.launchDateTime, granularity, locale)}
              </span>
            </span>
            <span className="block text-xs text-muted-foreground tabular-nums">
              {campaign.analytics.dataSufficiency.shouldHideData
                ? '—'
                : LL.home.revenue.campaignResult({
                    revenue: formatCurrency(
                      campaign.analytics.revenue,
                      locale,
                      currency
                    ),
                    count: campaign.analytics.paidOrderCount,
                  })}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
